import { useState, useEffect } from 'react';
import type { GeoLocation } from '../types';
import { captureException } from '../lib/sentry';

const CACHE_KEY = 'quul_geolocation';
const CACHE_DURATION_MS = 24 * 60 * 60 * 1000; // 24 hours

// Bot detection: skip API calls for crawlers to preserve rate limits
function isBot(): boolean {
  const ua = navigator.userAgent.toLowerCase();
  const botPatterns = [
    'bot', 'crawler', 'spider', 'headless', 'phantom', 'selenium',
    'puppeteer', 'playwright', 'googlebot', 'bingbot', 'slurp',
    'duckduckbot', 'baiduspider', 'yandexbot', 'facebookexternalhit',
    'twitterbot', 'linkedinbot', 'embedly', 'quora', 'pinterest',
    'redditbot', 'slackbot', 'discordbot', 'telegrambot', 'whatsapp',
  ];
  return botPatterns.some(pattern => ua.includes(pattern));
}

/**
 * Get cached location from sessionStorage.
 * Returns null on any failure (missing, expired, corrupted, or inaccessible storage).
 * Failures are expected in private browsing mode - treat as cache miss.
 */
function getCachedLocation(): GeoLocation | null {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (!cached) return null;

    const { data, timestamp } = JSON.parse(cached);
    const isExpired = Date.now() - timestamp > CACHE_DURATION_MS;

    if (isExpired) {
      sessionStorage.removeItem(CACHE_KEY);
      return null;
    }

    return data;
  } catch (error) {
    captureException(error, { hook: 'useGeolocation', action: 'cacheRead' });
    return null;
  }
}

/**
 * Save location to sessionStorage.
 * Returns false on failure - caching is an optimization, not required.
 * Expected failures: private browsing, quota exceeded, SecurityError.
 */
function cacheLocation(location: GeoLocation): boolean {
  try {
    sessionStorage.setItem(CACHE_KEY, JSON.stringify({
      data: location,
      timestamp: Date.now(),
    }));
    return true;
  } catch (error) {
    captureException(error, { hook: 'useGeolocation', action: 'cacheWrite' });
    return false;
  }
}

export function useGeolocation(): GeoLocation {
  const [location, setLocation] = useState<GeoLocation>(() => {
    // Try to restore from cache immediately
    const cached = getCachedLocation();
    if (cached) {
      return { ...cached, loading: false };
    }
    return {
      city: '',
      region: '',
      country: '',
      loading: true,
      error: null,
    };
  });

  useEffect(() => {
    // Skip if we already have cached data
    if (!location.loading) return;

    // Skip API calls for bots to preserve rate limits
    if (isBot()) {
      setLocation(prev => ({ ...prev, loading: false }));
      return;
    }

    const fetchLocation = async () => {
      try {
        // Using ipapi.co (free tier: 30k/month)
        const response = await fetch('https://ipapi.co/json/');

        if (!response.ok) {
          // Rate limit - actionable, report to Sentry
          if (response.status === 429) {
            captureException(new Error('ipapi.co rate limit exceeded (429)'), {
              hook: 'useGeolocation',
              action: 'rateLimitExceeded',
            });
          }
          // Server errors - report to track ipapi.co reliability
          else if (response.status >= 500) {
            captureException(new Error(`ipapi.co server error: ${response.status}`), {
              hook: 'useGeolocation',
              action: 'serverError',
              status: response.status,
            });
          }
          // All non-ok responses fail gracefully for UX
          setLocation(prev => ({
            ...prev,
            loading: false,
            error: 'Could not detect location',
          }));
          return;
        }

        const data = await response.json();

        const newLocation: GeoLocation = {
          city: data.city || 'Unknown',
          region: data.region || '',
          country: data.country_name || '',
          loading: false,
          error: null,
        };

        cacheLocation(newLocation);
        setLocation(newLocation);
      } catch (error) {
        captureException(error, { hook: 'useGeolocation', action: 'fetchLocation' });
        setLocation((prev) => ({
          ...prev,
          loading: false,
          error: 'Could not detect location',
        }));
      }
    };

    fetchLocation();
  }, [location.loading]);

  return location;
}
